import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AlertCircle, Check, RefreshCw, Send, X } from 'lucide-react';
import {
  useActivationGenerations,
  useApproveGeneration,
  useRejectGeneration,
  useRegenerateGeneration,
} from '@/hooks/useActivationResults';
import { useDeliverParticipant } from '@/hooks/useActivationParticipants';
import {
  GenerationStatusBadge,
  ApprovalStatusBadge,
} from '@/components/activations/statusBadges';
import type { ActivationGenerationAdminResponse } from '@/types/generated/activations.generated';

interface ApprovalReviewPanelProps {
  activationId: string;
}

interface ParticipantGroup {
  participantId: string;
  name: string;
  email: string | null;
  generations: ActivationGenerationAdminResponse[];
}

/**
 * Approval Review queue for an OperatorCurated photo booth. Generations are grouped per
 * guest; each card shows the output image, prompt, generation + approval status, and the
 * approve / reject / regenerate actions. A per-guest Send/Resend delivers the approved
 * bundle.
 */
export function ApprovalReviewPanel({ activationId }: ApprovalReviewPanelProps) {
  const generationsQuery = useActivationGenerations(activationId);
  const approve = useApproveGeneration(activationId);
  const reject = useRejectGeneration(activationId);
  const regenerate = useRegenerateGeneration(activationId);
  const deliver = useDeliverParticipant(activationId);

  const [rejectFor, setRejectFor] = useState<ActivationGenerationAdminResponse | null>(null);
  const [regenerateFor, setRegenerateFor] = useState<ActivationGenerationAdminResponse | null>(null);

  const groups = useMemo<ParticipantGroup[]>(() => {
    const map = new Map<string, ParticipantGroup>();
    for (const g of generationsQuery.data ?? []) {
      const key = g.activationParticipantId ?? 'unassigned';
      if (!map.has(key)) {
        map.set(key, {
          participantId: key,
          name: g.participantName || 'Unnamed guest',
          email: g.participantEmail ?? null,
          generations: [],
        });
      }
      map.get(key)!.generations.push(g);
    }
    return Array.from(map.values());
  }, [generationsQuery.data]);

  if (generationsQuery.isLoading) {
    return <Skeleton className="h-48 w-full" data-testid="approval-loading" />;
  }
  if (generationsQuery.isError) {
    return (
      <Alert variant="destructive" data-testid="approval-error">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Failed to load the review queue</AlertTitle>
        <AlertDescription>{(generationsQuery.error as Error)?.message}</AlertDescription>
      </Alert>
    );
  }
  if (groups.length === 0) {
    return (
      <Card className="flex flex-col items-center gap-2 p-12 text-center" data-testid="approval-empty">
        <p className="text-sm text-muted-foreground">No generations to review yet.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="approval-review-panel">
      {groups.map((group) => {
        const hasApproved = group.generations.some((g) => g.approvalStatus === 'Approved');
        return (
          <Card key={group.participantId} data-testid={`approval-group-${group.participantId}`}>
            <CardContent className="space-y-4 py-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-medium text-foreground">{group.name}</p>
                  {group.email && <p className="text-xs text-muted-foreground">{group.email}</p>}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!hasApproved || deliver.isPending || group.participantId === 'unassigned'}
                  onClick={() => deliver.mutate(group.participantId)}
                  data-testid={`deliver-${group.participantId}`}
                >
                  <Send className="mr-2 h-4 w-4" />
                  Send / Resend
                </Button>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {group.generations.map((g) => (
                  <Card key={g.id} data-testid={`approval-card-${g.id}`}>
                    <div
                      className="flex aspect-video items-center justify-center overflow-hidden rounded-t-lg bg-muted"
                      data-testid={`approval-media-${g.id}`}
                      data-media-type={g.mediaType}
                    >
                      {g.mediaType === 'Video' ? (
                        g.outputBlobUrl ? (
                          <video
                            src={g.outputBlobUrl}
                            poster={g.thumbnailUrl ?? undefined}
                            controls
                            preload="metadata"
                            className="h-full w-full object-contain"
                            data-testid={`approval-video-${g.id}`}
                          />
                        ) : (
                          <span className="text-xs text-muted-foreground">No output yet</span>
                        )
                      ) : g.outputBlobUrl || g.thumbnailUrl ? (
                        <img
                          src={g.thumbnailUrl || g.outputBlobUrl!}
                          alt={g.prompt || 'Generated media'}
                          className="h-full w-full object-contain"
                          loading="lazy"
                          data-testid={`approval-image-${g.id}`}
                        />
                      ) : (
                        <span className="text-xs text-muted-foreground">No output yet</span>
                      )}
                    </div>
                    <CardContent className="space-y-2 py-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <GenerationStatusBadge status={g.generationStatus} />
                        <ApprovalStatusBadge status={g.approvalStatus} />
                      </div>
                      {g.prompt && (
                        <p className="line-clamp-3 text-xs text-muted-foreground" title={g.prompt}>
                          {g.prompt}
                        </p>
                      )}
                      {g.rejectedReason && (
                        <p className="text-xs text-destructive">Rejected: {g.rejectedReason}</p>
                      )}
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={
                            approve.isPending ||
                            g.generationStatus !== 'Succeeded' ||
                            g.approvalStatus === 'Approved'
                          }
                          onClick={() => approve.mutate(g.id)}
                          data-testid={`approve-${g.id}`}
                        >
                          <Check className="mr-1 h-3.5 w-3.5" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-destructive"
                          disabled={reject.isPending || g.approvalStatus === 'Rejected'}
                          onClick={() => setRejectFor(g)}
                          data-testid={`reject-${g.id}`}
                        >
                          <X className="mr-1 h-3.5 w-3.5" />
                          Reject
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={regenerate.isPending}
                          onClick={() => setRegenerateFor(g)}
                          data-testid={`regenerate-${g.id}`}
                        >
                          <RefreshCw className="mr-1 h-3.5 w-3.5" />
                          Regenerate
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {rejectFor && (
        <RejectDialog
          generation={rejectFor}
          isPending={reject.isPending}
          onCancel={() => setRejectFor(null)}
          onConfirm={(reason) => {
            reject.mutate(
              { generationId: rejectFor.id, data: { reason: reason || null } },
              { onSuccess: () => setRejectFor(null) },
            );
          }}
        />
      )}

      {regenerateFor && (
        <RegenerateDialog
          generation={regenerateFor}
          isPending={regenerate.isPending}
          onCancel={() => setRegenerateFor(null)}
          onConfirm={(prompt) => {
            regenerate.mutate(
              { generationId: regenerateFor.id, data: { prompt: prompt || null } },
              { onSuccess: () => setRegenerateFor(null) },
            );
          }}
        />
      )}
    </div>
  );
}

function RejectDialog({
  generation,
  isPending,
  onCancel,
  onConfirm,
}: {
  generation: ActivationGenerationAdminResponse;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent data-testid="reject-dialog">
        <DialogHeader>
          <DialogTitle>Reject this asset</DialogTitle>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="reject-reason">Reason (optional)</Label>
          <Textarea
            id="reject-reason"
            data-testid="reject-reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={isPending}
            onClick={() => onConfirm(reason.trim())}
            data-testid="reject-confirm"
          >
            {isPending ? 'Rejecting…' : 'Reject'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RegenerateDialog({
  generation,
  isPending,
  onCancel,
  onConfirm,
}: {
  generation: ActivationGenerationAdminResponse;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: (prompt: string) => void;
}) {
  const [prompt, setPrompt] = useState(generation.prompt ?? '');
  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent data-testid="regenerate-dialog">
        <DialogHeader>
          <DialogTitle>Regenerate this asset</DialogTitle>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="regenerate-prompt">Prompt (optional — defaults to the original)</Label>
          <Textarea
            id="regenerate-prompt"
            data-testid="regenerate-prompt"
            rows={3}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            disabled={isPending}
            onClick={() => onConfirm(prompt.trim())}
            data-testid="regenerate-confirm"
          >
            {isPending ? 'Regenerating…' : 'Regenerate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
